import { ReactNode } from 'react';
import Box, { BoxProps } from '../Box/Box';
import Icon, { TypeIconName } from '../Icon/Icon';
import { TooltipProps } from '../Tooltip/Tooltip';
import Text, { TextProps } from '../Text/Text';
import Label from '../Label/Label';
import Skeleton from '../Skeleton/Skeleton';
import SkeletonBox from '../Skeleton/SkeletonBox';

type DataOrPlaceholder = { placeholder?: string; data?: never } | { data?: string; placeholder?: never };
export type LabeledDataProps = BoxProps & {
  skeletonVariant?: 'single' | 'double';
  as?: 'div' | 'dl';
  isLoading?: boolean;
  label: string;
  secondaryLabel?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  skeleton?: ReactNode;
  shouldShowTrendSign?: boolean;
  tooltip?: Omit<TooltipProps, 'children'>;
  trend?: 'positive' | 'negative' | 'neutral';
  trendColorScheme?: 'red' | 'green' | 'grey';
  trendValue?: string;
} & DataOrPlaceholder;

const LabeledData = ({
  as = 'div',
  skeletonVariant = 'double',
  data,
  placeholder,
  isLoading = false,
  label,
  secondaryLabel,
  size = 'sm',
  skeleton,
  shouldShowTrendSign = true,
  tooltip,
  trend,
  trendValue,
  trendColorScheme,
  ...rest
}: LabeledDataProps): JSX.Element => {
  const trendIconSize = size === 'sm' ? '16' : '24';
  const labelColor = size === 'sm' ? 'text/primary' : 'text/secondary';
  let trendSign;
  if (shouldShowTrendSign) {
    trendSign = trend === 'negative' ? '-' : '+';
  }

  let dataSkeletonHeight;
  let dataSkeletonPaddingY;
  switch (size) {
    case 'lg':
      dataSkeletonHeight = '30px';
      dataSkeletonPaddingY = '5';
      break;
    case 'md':
      dataSkeletonHeight = '24';
      dataSkeletonPaddingY = '6';
      break;
    case 'sm':
    default:
      dataSkeletonHeight = '16';
      dataSkeletonPaddingY = '4';
  }

  let trendIconName: TypeIconName | undefined;
  switch (trend) {
    case 'positive':
      trendIconName = 'ArrowUp';
      break;
    case 'negative':
      trendIconName = 'ArrowDown';
      break;
    case 'neutral':
      trendIconName = 'Minus';
      break;
  }

  let trendIconColor: string | undefined;
  let trendColor: TextProps['color'] | undefined;
  switch (trendColorScheme) {
    case 'red':
      trendColor = 'text/negative';
      trendIconColor = 'icon/negative';
      break;
    case 'green':
      trendIconColor = 'icon/positive';
      trendColor = 'text/positive';
      break;
    default:
      trendColor = 'text/secondary';
      trendIconColor = 'icon/tertiary';
  }

  if (isLoading && skeleton) {
    return <>{skeleton}</>;
  }

  return (
    <Box as={as} display="flex" flexDirection="column" {...rest}>
      <Label as="dt" color={labelColor} tooltip={tooltip}>
        {label}
      </Label>
      {isLoading ? (
        <Skeleton isActive display="flex" flexDirection="column" gap="4">
          <SkeletonBox height={dataSkeletonHeight} width="120px" marginY={dataSkeletonPaddingY} />
          {skeletonVariant === 'double' && <SkeletonBox height="14" width="120px" marginY="3" />}
        </Skeleton>
      ) : (
        <>
          {data && (
            <Box as="dd" display="flex" alignItems="center" gap="4" color="text/body" textStyle={`comp/data/${size}`}>
              {data}
              {trendIconName && trendValue && <Icon color={trendIconColor} name={trendIconName} size={trendIconSize} />}
            </Box>
          )}
          {placeholder && (
            <Box
              as="dd"
              display="flex"
              alignItems="center"
              gap="4"
              color="text/tertiary"
              textStyle={`comp/data/${size}`}
            >
              {placeholder}
            </Box>
          )}
          {(trendValue || !!secondaryLabel) && (
            <Box as="dd" display="flex" gap="4" color={trendColor} textStyle="body/md/regular">
              {trendValue && (
                <>
                  {trendSign}
                  {trendValue}
                </>
              )}
              {secondaryLabel && (
                <Text as="span" color="text/tertiary">
                  {secondaryLabel}
                </Text>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default LabeledData;
